import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { MapPin, Loader2 } from 'lucide-react';
import { useGeolocation } from '../lib/use-geolocation';

export interface NearMeButtonProps {
  /** Called with the user's coordinates once geolocation succeeds. */
  onLocated: (lat: number, lng: number) => void;
}

export function NearMeButton({ onLocated }: NearMeButtonProps) {
  const { t } = useTranslation();
  const geo = useGeolocation();
  const firedRef = useRef(false);

  // Fire onLocated exactly once per successful lookup, regardless of whether
  // the parent passes a stable callback. The ref is reset when a new lookup
  // starts so a second "Near me" tap works.
  useEffect(() => {
    if (geo.status === 'locating') {
      firedRef.current = false;
    }
  }, [geo.status]);

  useEffect(() => {
    if (geo.status === 'granted' && geo.coords && !firedRef.current) {
      firedRef.current = true;
      onLocated(geo.coords.lat, geo.coords.lng);
    }
  }, [geo.status, geo.coords, onLocated]);

  const locating = geo.status === 'locating';
  const failed = geo.status === 'denied' || geo.status === 'unavailable';

  return (
    <div className="inline-flex flex-col items-start gap-1">
      <button
        type="button"
        onClick={() => geo.request()}
        disabled={locating}
        aria-label={t('search.nearMe')}
        className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 bg-card text-xs font-medium text-foreground hover:border-primary/40 hover:text-primary transition-colors disabled:opacity-60"
      >
        {locating ? (
          <Loader2 className="w-3.5 h-3.5 animate-spin" />
        ) : (
          <MapPin className="w-3.5 h-3.5" />
        )}
        {locating ? t('search.locating') : t('search.nearMe')}
      </button>
      {failed && (
        <span className="text-xs text-muted-foreground">{t('search.locationDenied')}</span>
      )}
    </div>
  );
}
